import type { ScheduledTrainingReportDto } from '../../../../API/rtk-query-api/store/tmsApi'
import type { DropdownOption } from '../../../UI/multi-select-dropdown/MultiSelectDropdown'

export const ScheduleType = {
  Daily: 0,
  Weekly: 1,
  Monthly: 2,
  Yearly: 3
} as const
export type ScheduleType = (typeof ScheduleType)[keyof typeof ScheduleType]

// Helper to get the key name from a ScheduleType value
export const ScheduleTypeName: Record<ScheduleType, keyof typeof ScheduleType> = {
  [ScheduleType.Daily]: 'Daily',
  [ScheduleType.Weekly]: 'Weekly',
  [ScheduleType.Monthly]: 'Monthly',
  [ScheduleType.Yearly]: 'Yearly'
}

export const DayOfWeekEnum = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6
} as const
export type DayOfWeekEnum = (typeof DayOfWeekEnum)[keyof typeof DayOfWeekEnum]

export const MonthEnum = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11
} as const
export type MonthEnum = (typeof MonthEnum)[keyof typeof MonthEnum]

export interface ScheduleRules {
  id: string
  type: ScheduleType
}

export function scheduleTypeAsDropdownItems(): DropdownOption[] {
  return (Object.keys(ScheduleType) as Array<keyof typeof ScheduleType>).map(key => ({
    text: key,
    value: String(ScheduleType[key])
  }))
}

export function dayOfWeekEnumAsDropdownItems(): DropdownOption[] {
  return (Object.keys(DayOfWeekEnum) as Array<keyof typeof DayOfWeekEnum>).map(key => ({
    text: key,
    value: String(DayOfWeekEnum[key])
  }))
}

export function monthEnumAsDropdownItems(): DropdownOption[] {
  return (Object.keys(MonthEnum) as Array<keyof typeof MonthEnum>).map(key => ({
    text: key,
    value: String(MonthEnum[key])
  }))
}

export function getAllTimezones(): Array<DropdownOption> {
  return Intl.supportedValuesOf('timeZone').map(timezone => {
    return { text: timezone, value: timezone }
  })
}

export function getValidationErrorMessage(field: string) {
  return `${field} is a required field`
}

export function modifyReportToForm(report: ScheduledTrainingReportDto) {
  const timeZone = { text: report.timeZone, value: report.timeZone }
  const rules = report.rules?.map(rule => {
    const type = toFirstLetterUpperCase(rule.interval)
    const dayOfWeek = toFirstLetterUpperCase(rule.dayOfWeek)
    const month = toFirstLetterUpperCase(rule.month)
    return {
      type: { text: type, value: String(ScheduleType[type as keyof typeof ScheduleType]) },
      dayOfWeek: dayOfWeek
        ? {
            text: dayOfWeek,
            value: String(DayOfWeekEnum[dayOfWeek as keyof typeof DayOfWeekEnum])
          }
        : undefined,
      month: month ? { text: month, value: String(MonthEnum[month as keyof typeof MonthEnum]) } : undefined,
      hour: rule.hourOfDay,
      day: rule.dayOfMonth
    }
  })

  return {
    reportName: report.name,
    subject: report.subject,
    recipients: report.recipients,
    timezone: timeZone,
    rules: rules,
    view: {}
  }
}

function toFirstLetterUpperCase(value: string | undefined) {
  if (value) {
    return value.charAt(0) + value.slice(1).toLowerCase()
  }
}
